import { useMemo, useState } from 'react';
import { useListFetch, useModal, useRequest, useSort } from '../../hooks';
import { notify } from '../../app';
import {
  updateSystemPrompt,
  createSystemPrompt,
  deleteSystemPrompt,
  getAllSystemPrompts,
} from '../../api';
import type {
  AgentCreateRequest,
  SystemPrompt,
  SystemPromptCreate,
  SystemPromptUpdate,
} from '../../api';
import { sortArray } from '../../utilities';

export type AgentFormType = AgentCreateRequest;

export const usePromptLibrary = () => {
  const { isOpened, onClose, onOpen } = useModal();
  const {
    isOpened: isOpenedDeleteModal,
    onClose: onCloseDeleteModal,
    onOpen: onOpenDeleteModal,
  } = useModal();
  const { sort, setSort } = useSort({ name: 'title', direction: 'asc' }, false);

  const [selectedPrompt, setSelectedPrompt] = useState<SystemPrompt | null>(
    null
  );
  const { nodes, filters, setFilters, intializing, refresh, ...rest } =
    useListFetch<SystemPrompt>(
      async () => {
        const arr = await getAllSystemPrompts();
        return { content: arr };
      },
      {
        deps: [],
        withQueryParams: true,
      }
    );

  const [handleCreatePrompt, creatingPrompt] = useRequest(
    async (_signal, payload: SystemPromptCreate) => {
      await createSystemPrompt(payload);
    },
    {
      onSuccess: () => {
        notify.success('The prompt created successfully');
        refresh();
        onClose?.();
        setSelectedPrompt(null);
      },
      onError: () => {
        notify.error('Failed to create the prompt');
      },
      deps: [],
    }
  );

  const [handleUpdatePrompt, updatingPrompt] = useRequest(
    async (_signal, payload: SystemPrompt & { id: string }) => {
      const { id, ...updateData } = payload;
      await updateSystemPrompt(id, updateData as SystemPromptUpdate);
    },
    {
      onSuccess: () => {
        notify.success('The prompt updated successfully');
        refresh();
        onClose?.();
        setSelectedPrompt(null);
      },
      onError: () => {
        notify.error('Failed to update the prompt');
      },
      deps: [],
    }
  );

  const [handleDeletePrompt, deletingPrompt] = useRequest(
    (_signal, id: string) => deleteSystemPrompt(id),
    {
      onSuccess: () => {
        notify.success('The prompt deleted successfully');
        refresh();
        onCloseDeleteModal?.();
      },
      onError: () => {
        notify.error('Failed to delete the prompt');
      },
      deps: [],
    }
  );

  const sortedPrompts = useMemo(() => {
    return sortArray(nodes as SystemPrompt[], sort);
  }, [nodes, sort]);

  return {
    prompts: sortedPrompts,
    deletingPrompt,
    handleDeletePrompt,
    isOpenedDeleteModal,
    onCloseDeleteModal,
    onOpenDeleteModal,
    handleCreatePrompt,
    handleUpdatePrompt,
    selectedPrompt,
    setSelectedPrompt,
    loadingPropmpt: updatingPrompt && creatingPrompt,
    isOpened,
    onClose,
    onOpen,
    setAgentsSort: setSort,
    sortPrompts: sort,
    ...rest,
  };
};
